import { GuidePriority } from "../guide";
import type { GuideContext, GuideStep } from "../guide";

/**
 * Captain Bram on the pets (docs/PETS.md, Pets v2): once a pet is caught and the battle card shows its chip,
 * one line pointing at it — once per save, target required, so it never speaks before a pet is there (the empty
 * chip before any catch is not his target). In battle, his first sight of a wild pet (the catch card carries his
 * cheer itself: ui/pets/petMomentUi.ts).
 */

export const PetsTargets = {
  chip: ".hub .battlecard .prebattle .petchip:not(.nopet)",
  /** The TAP! pill over the wild pet while it romps (level/levelPet.ts; the tag itself is a zero-size anchor). */
  wildTag: ".wildtag:not(.hidden) > span",
} as const;

/** Hub feature tips wait for the first session to be over (scripts/features.ts HubTipsFromCleared). */
const FROM_CLEARED = 2;
const TIP = { kind: "tip", block: "none", needsTarget: true } as const;
const lobby = (c: GuideContext): boolean => c.screen === "hub" && c.hub !== null && c.hub.sheet === null && c.hub.highestCleared >= FROM_CLEARED;

export const petsHubSteps: readonly GuideStep[] = [
  { ...TIP, id: "hub.pet", screen: "hub", pose: "pointing", priority: GuidePriority.NewThing, topic: "pets",
    target: { dom: PetsTargets.chip },
    text: "Our first pet! It fights at the flag: upgrade it on the **Towers** tab, or tap here to pick who comes along.",
    when: lobby },
];

const inLevel = (c: GuideContext): boolean => c.screen === "level" && c.level !== null;

/** Captain Bram in battle on his first sight of a wild pet, pointing at it. Once per save. */
export const petsLevelSteps: readonly GuideStep[] = [
  { ...TIP, id: "level.wildPet", screen: "level", pose: "pointing", priority: GuidePriority.NewThing, topic: "pets",
    target: { dom: PetsTargets.wildTag },
    text: "A wild pet! Tap it if you fancy a chase — catch it and it joins us.",
    when: (c) => inLevel(c) && (c.level?.wildPetOut === true || c.signals.has("wildPetAppeared")),
    // tapped (or gone): the line has done its job
    until: (c) => c.level !== null && !c.level.wildPetOut && !c.signals.has("wildPetAppeared") },
];
